import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import api from '../services/api';
import { ArrowLeft, User, Wifi, Calendar, DollarSign, CreditCard, AlertTriangle, CheckCircle } from 'lucide-react';

const PPPoEClientDetailPage = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isDark } = useTheme();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    payment_method: 'cash',
    reference: '',
    notes: ''
  });

  useEffect(() => {
    loadClient();
  }, [clientId]);

  const loadClient = async () => {
    try {
      const response = await api.get(`/api/pppoe/clients/${clientId}`);
      setClient(response.data);
      // Pre-llenar el monto con el pago mensual
      setPaymentForm(prev => ({
        ...prev,
        amount: response.data.monthly_payment.toString()
      }));
    } catch (error) {
      console.error('Error cargando cliente:', error);
      alert('Error al cargar información del cliente');
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterPayment = async (e) => {
    e.preventDefault();
    try {
      const data = {
        client_id: clientId,
        amount: parseFloat(paymentForm.amount),
        payment_method: paymentForm.payment_method,
        reference: paymentForm.reference,
        notes: paymentForm.notes
      };

      const response = await api.post('/api/pppoe/payments', data);
      
      let message = '✅ Pago registrado exitosamente';
      if (response.data.auto_reactivated) {
        message += '\n🔄 Cliente reactivado automáticamente';
      }
      
      alert(message);
      setShowPaymentModal(false);
      loadClient();
    } catch (error) {
      console.error('Error registrando pago:', error);
      alert('❌ Error al registrar pago');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className={isDark ? 'text-white' : 'text-gray-900'}>Cliente no encontrado</p>
      </div>
    );
  }

  const isOverdue = client.next_due_date && new Date(client.next_due_date) < new Date();

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      isDark 
        ? 'bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900' 
        : 'bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50'
    }`}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6 flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/pppoe')}
              className={`p-2 rounded-lg ${isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-100'}`}
            >
              <ArrowLeft className={isDark ? 'text-white' : 'text-gray-900'} />
            </button>
            <div>
              <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {client.name}
              </h1>
              <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                Cliente PPPoE - {client.pppoe_username}
              </p>
            </div>
          </div>

          <button
            onClick={() => setShowPaymentModal(true)}
            className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 py-3 rounded-lg font-semibold hover:shadow-xl transition-all flex items-center gap-2"
          >
            <DollarSign className="w-5 h-5" />
            Registrar Pago
          </button>
        </div>

        {/* Status Alert */}
        {isOverdue && client.status === 'active' && (
          <div className="mb-6 bg-yellow-900/30 border-2 border-yellow-600 rounded-xl p-4 flex items-center gap-3">
            <AlertTriangle className="w-6 h-6 text-yellow-400" />
            <div>
              <p className="text-yellow-400 font-semibold">Pago Vencido</p>
              <p className="text-yellow-300 text-sm">
                Este cliente tiene un pago pendiente. Se suspenderá automáticamente después de {client.plan_info?.grace_days || 3} días de gracia.
              </p>
            </div>
          </div>
        )}

        {client.status === 'suspended' && (
          <div className="mb-6 bg-red-900/30 border-2 border-red-600 rounded-xl p-4 flex items-center gap-3">
            <AlertTriangle className="w-6 h-6 text-red-400" />
            <div>
              <p className="text-red-400 font-semibold">Cliente Suspendido</p>
              <p className="text-red-300 text-sm">
                El servicio está deshabilitado. Registra un pago para reactivarlo automáticamente.
              </p>
            </div>
          </div>
        )}

        {/* Client Info Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Información Personal */}
          <div className={`p-6 rounded-xl shadow-lg ${
            isDark ? 'bg-gray-800/50' : 'bg-white'
          }`}>
            <h2 className={`text-xl font-bold mb-4 flex items-center gap-2 ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>
              <User className="w-5 h-5" />
              Información Personal
            </h2>
            <div className="space-y-3">
              <div>
                <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Teléfono:</span>
                <p className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{client.phone}</p>
              </div>
              {client.email && (
                <div>
                  <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Email:</span>
                  <p className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{client.email}</p>
                </div>
              )}
              {client.address && (
                <div>
                  <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Dirección:</span>
                  <p className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{client.address}</p>
                </div>
              )}
              <div>
                <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Estado:</span>
                <div className="mt-1">
                  {client.status === 'active' ? (
                    <span className="bg-green-500 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1 w-fit">
                      <CheckCircle className="w-3 h-3" />
                      Activo
                    </span>
                  ) : (
                    <span className="bg-red-500 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1 w-fit">
                      <AlertTriangle className="w-3 h-3" />
                      Suspendido
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Información del Servicio */}
          <div className={`p-6 rounded-xl shadow-lg ${
            isDark ? 'bg-gray-800/50' : 'bg-white'
          }`}>
            <h2 className={`text-xl font-bold mb-4 flex items-center gap-2 ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>
              <Wifi className="w-5 h-5" />
              Información del Servicio
            </h2>
            <div className="space-y-3">
              <div>
                <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Plan:</span>
                <p className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {client.plan_info?.name || 'N/A'}
                </p>
              </div>
              <div>
                <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Velocidad:</span>
                <p className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {client.plan_info?.download_speed}/{client.plan_info?.upload_speed}
                </p>
              </div>
              <div>
                <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Usuario PPPoE:</span>
                <p className={`font-semibold font-mono ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                  {client.pppoe_username}
                </p>
              </div>
              <div>
                <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Mensualidad:</span>
                <p className={`text-2xl font-bold ${isDark ? 'text-green-400' : 'text-green-600'}`}>
                  ${client.monthly_payment}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Info */}
        <div className={`p-6 rounded-xl shadow-lg mb-6 ${
          isDark ? 'bg-gray-800/50' : 'bg-white'
        }`}>
          <h2 className={`text-xl font-bold mb-4 flex items-center gap-2 ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>
            <Calendar className="w-5 h-5" />
            Información de Pagos
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Día de pago:</span>
              <p className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Día {client.payment_day} de cada mes
              </p>
            </div>
            <div>
              <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Próximo pago:</span>
              <p className={`font-semibold ${isOverdue ? 'text-red-500' : isDark ? 'text-white' : 'text-gray-900'}`}>
                {client.next_due_date ? new Date(client.next_due_date).toLocaleDateString('es-MX', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                }) : 'N/A'}
              </p>
            </div>
            <div>
              <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Último pago:</span>
              <p className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {client.last_payment_date ? new Date(client.last_payment_date).toLocaleDateString('es-MX') : 'Sin pagos'}
              </p>
            </div>
          </div>
        </div>

        {/* Payment History */}
        <div className={`p-6 rounded-xl shadow-lg ${
          isDark ? 'bg-gray-800/50' : 'bg-white'
        }`}>
          <h2 className={`text-xl font-bold mb-4 flex items-center gap-2 ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>
            <CreditCard className="w-5 h-5" />
            Historial de Pagos
          </h2>

          {!client.payment_history || client.payment_history.length === 0 ? (
            <p className={`text-center py-6 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              No hay pagos registrados
            </p>
          ) : (
            <div className="space-y-3">
              {client.payment_history.map((payment) => (
                <div
                  key={payment.id}
                  className={`p-4 rounded-lg border ${
                    isDark
                      ? 'bg-gray-700/50 border-gray-600'
                      : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <p className={`font-semibold text-lg ${isDark ? 'text-green-400' : 'text-green-600'}`}>
                        ${payment.amount}
                      </p>
                      <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        {new Date(payment.payment_date).toLocaleDateString('es-MX', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                      {payment.payment_method && (
                        <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                          Método: {payment.payment_method === 'cash' ? 'Efectivo' : payment.payment_method === 'transfer' ? 'Transferencia' : 'Tarjeta'}
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      {payment.reference && (
                        <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          Ref: {payment.reference}
                        </p>
                      )}
                      {payment.notes && (
                        <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                          {payment.notes}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Payment Modal */}
        {showPaymentModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className={`max-w-2xl w-full rounded-xl shadow-2xl ${
              isDark ? 'bg-gray-800' : 'bg-white'
            }`}>
              <div className="p-6">
                <h2 className={`text-2xl font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Registrar Pago - {client.name}
                </h2>

                <form onSubmit={handleRegisterPayment} className="space-y-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Monto a Pagar *
                    </label>
                    <input
                      type="number"
                      required
                      step="0.01"
                      value={paymentForm.amount}
                      onChange={(e) => setPaymentForm({...paymentForm, amount: e.target.value})}
                      className={`w-full px-4 py-2 rounded-lg border text-lg font-semibold ${
                        isDark 
                          ? 'bg-gray-700 border-gray-600 text-white' 
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                      placeholder="299.00"
                    />
                    <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      Mensualidad: ${client.monthly_payment}
                    </p>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Método de Pago *
                    </label>
                    <select
                      required
                      value={paymentForm.payment_method}
                      onChange={(e) => setPaymentForm({...paymentForm, payment_method: e.target.value})}
                      className={`w-full px-4 py-2 rounded-lg border ${
                        isDark 
                          ? 'bg-gray-700 border-gray-600 text-white' 
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                    >
                      <option value="cash">Efectivo</option>
                      <option value="transfer">Transferencia</option>
                      <option value="card">Tarjeta</option>
                    </select>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Referencia
                    </label>
                    <input
                      type="text"
                      value={paymentForm.reference}
                      onChange={(e) => setPaymentForm({...paymentForm, reference: e.target.value})}
                      className={`w-full px-4 py-2 rounded-lg border ${
                        isDark 
                          ? 'bg-gray-700 border-gray-600 text-white' 
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                      placeholder="Número de transacción o referencia"
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Notas
                    </label>
                    <textarea
                      value={paymentForm.notes}
                      onChange={(e) => setPaymentForm({...paymentForm, notes: e.target.value})}
                      rows="2"
                      className={`w-full px-4 py-2 rounded-lg border ${
                        isDark 
                          ? 'bg-gray-700 border-gray-600 text-white' 
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                      placeholder="Notas adicionales..."
                    />
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowPaymentModal(false)}
                      className={`flex-1 px-6 py-3 rounded-lg font-semibold transition-colors ${
                        isDark
                          ? 'bg-gray-700 hover:bg-gray-600 text-white'
                          : 'bg-gray-200 hover:bg-gray-300 text-gray-900'
                      }`}
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 py-3 rounded-lg font-semibold hover:shadow-xl transition-all"
                    >
                      Registrar Pago
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PPPoEClientDetailPage;
